import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import SearchIcon from '../../../../../../assets/Svg/SearchIcon';
import ReactDOM from 'react-dom';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import styles from './RelatedCompaniesNetwork.module.scss';
import {
    BLACK_DROPDOWN_CARET,
    caretRight,
    filterIconMUDark,
    filterIconMUPurpleDark,
    purpleOfficeIcon,
    purpleRedirectIcon,
} from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import OfficeIcon from '../../../../../../assets/Svg/OfficeIcon';
import { AICA_REPORT_AVAILABLE_TOOLTIP } from '../../../../../../utils/constants/commonConstants';
import clsx from 'clsx';
import { CompaniesNetworkData, NetworkRelatedPartyEntity, RelatedCompany, StatusOption } from './types';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_NETWORK_FILTER_APPLIED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useAppSelector } from '../../../../../../app/hooks';
import RelatedPartiesTab from './RelatedPartiesTab';
import greyLock from '../../../../../../assets/grey-lock-2.svg';
import { SOURCE_LABELS, formatLenderDate } from '../../../../../../utils/lenderConnectionUtils';

interface RelatedCompaniesNetworkDrawerProps {
    data: CompaniesNetworkData;
    lenderConnections?: any[];
    statusOptions: StatusOption[];
    relationshipStatusOptions: StatusOption[];
    onClose: () => void;
    companyName?: string;
    isRelatedPartiesLocked?: boolean;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function getStatusMeta(status: string | undefined, options: StatusOption[]): StatusOption | null {
    if (!status) return null;
    return options.find((opt) => opt?.value?.toLowerCase?.() === status?.toLowerCase?.()) ?? null;
}

function formatDate(timestamp: number | undefined): string {
    if (!timestamp) return '-';
    const d = new Date(timestamp);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function toRelationshipLabel(relationshipType: string): string {
    return relationshipType
        .split('_')
        .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
        .join(' ');
}

// ─── Filter hook ─────────────────────────────────────────────────────────────

type FilterKey = 'status' | 'relationship';

function useDrawerFilters(tableRef: React.RefObject<HTMLDivElement>) {
    const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [relationshipFilters, setRelationshipFilters] = useState<string[]>([]);
    const [filterSearch, setFilterSearch] = useState('');
    const statusFilterBtnRef = useRef<HTMLButtonElement>(null);
    const relationshipFilterBtnRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!activeFilter) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const isStatusBtn = statusFilterBtnRef.current?.contains(target);
            const isRelBtn = relationshipFilterBtnRef.current?.contains(target);
            const isDropdown = !!(e.target as Element)?.closest?.('[data-filter-dropdown]');
            if (!isStatusBtn && !isRelBtn && !isDropdown) {
                setActiveFilter(null);
                setFilterSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeFilter]);

    useEffect(() => {
        const tableEl = tableRef.current;
        if (!tableEl) return;
        const handleScroll = () => {
            setActiveFilter(null);
            setFilterSearch('');
        };
        tableEl.addEventListener('scroll', handleScroll);
        return () => tableEl.removeEventListener('scroll', handleScroll);
    }, [tableRef]);

    const toggleFilter = (type: FilterKey) => {
        setActiveFilter((prev) => (prev === type ? null : type));
        setFilterSearch('');
    };

    const toggleStatusFilter = (value: string) => {
        const lower = value.toLowerCase();
        setStatusFilters((prev) => (prev.includes(lower) ? prev.filter((v) => v !== lower) : [...prev, lower]));
    };

    const toggleRelationshipFilter = (value: string) => {
        const lower = value.toLowerCase();
        setRelationshipFilters((prev) => (prev.includes(lower) ? prev.filter((v) => v !== lower) : [...prev, lower]));
    };

    return {
        activeFilter,
        statusFilters,
        relationshipFilters,
        filterSearch,
        setFilterSearch,
        statusFilterBtnRef,
        relationshipFilterBtnRef,
        toggleFilter,
        toggleStatusFilter,
        toggleRelationshipFilter,
    };
}

type DrawerTab = 'customersVendors' | 'lenders' | 'relatedParties';

function formatTabCount(n: number): string {
    if (n === 0) return '0';
    return String(n).padStart(2, '0');
}

// ─── Main component ───────────────────────────────────────────────────────────

const RelatedCompaniesNetworkDrawer: React.FC<RelatedCompaniesNetworkDrawerProps> = ({
    data,
    lenderConnections,
    statusOptions,
    relationshipStatusOptions,
    onClose,
    companyName,
    isRelatedPartiesLocked = false,
}) => {
    const [activeTab, setActiveTab] = useState<DrawerTab>('customersVendors');
    const [vendorsExpanded, setVendorsExpanded] = useState(true);
    const [customersExpanded, setCustomersExpanded] = useState(true);
    const tableRef = useRef<HTMLDivElement>(null);

    const userData = useAppSelector((state) => state.user.userData);

    const {
        activeFilter,
        statusFilters,
        relationshipFilters,
        filterSearch,
        setFilterSearch,
        statusFilterBtnRef,
        relationshipFilterBtnRef,
        toggleFilter,
        toggleStatusFilter,
        toggleRelationshipFilter,
    } = useDrawerFilters(tableRef);

    const trackFilterApplied = (filterValue: string) => {
        Mixpanel.track(AICA_NETWORK_FILTER_APPLIED, {
            userEmail: userData?.primaryEmail,
            companyName,
            pnoId: userData?.investorOrgId,
            filterValue,
        });
    };

    const handleToggleStatusFilter = (value: string) => {
        toggleStatusFilter(value);
        trackFilterApplied(value);
    };

    const handleToggleRelationshipFilter = (value: string) => {
        toggleRelationshipFilter(value);
        trackFilterApplied(value);
    };

    const vendorCount = data.vendorFor?.length ?? 0;
    const customerCount = data.customerOf?.length ?? 0;
    const cvCount = vendorCount + customerCount;
    const lenderCount = (lenderConnections || []).length;

    const filteredVendors = (data.vendorFor ?? []).filter((v) => {
        const statusMatch = statusFilters.length === 0 || statusFilters.includes(v.orgStatus?.toLowerCase() ?? '');
        const relMatch = relationshipFilters.length === 0 || relationshipFilters.includes(v.orgRelationshipStatus?.toLowerCase() ?? '');
        return statusMatch && relMatch;
    });

    const filteredCustomers = (data.customerOf ?? []).filter((c) => {
        const statusMatch = statusFilters.length === 0 || statusFilters.includes(c.orgStatus?.toLowerCase() ?? '');
        const relMatch = relationshipFilters.length === 0 || relationshipFilters.includes(c.orgRelationshipStatus?.toLowerCase() ?? '');
        return statusMatch && relMatch;
    });

    const relatedPartiesCount = useMemo(() => {
        return Object.values(data.networkRelatedPartyEntities ?? {}).filter((cm) => (cm.matches?.length ?? 0) > 0).length;
    }, [data.networkRelatedPartyEntities]);
    const lenderList = lenderConnections || [];

    const activeFilterOptions = activeFilter === 'status' ? statusOptions : relationshipStatusOptions;
    const activeFilterValues = activeFilter === 'status' ? statusFilters : relationshipFilters;
    const visibleFilterOptions = filterSearch
        ? activeFilterOptions.filter((opt) => opt.label.toLowerCase().includes(filterSearch.toLowerCase()))
        : activeFilterOptions;

    const formatAmount = (amount: number | undefined | null): string => {
        if (!amount || amount === 0) return '-';
        return `₹${(amount / 10000000).toFixed(2)} Cr`;
    };

    return (
        <SideDrawer onClose={onClose} width="80%" iconStyle={{ marginRight: '-17px' }}>
            <div className={styles.DrawerContent}>
                <div className={styles.DrawerTabs}>
                    <button
                        type="button"
                        className={clsx(styles.DrawerTab, activeTab === 'customersVendors' && styles.DrawerTabActive)}
                        onClick={() => setActiveTab('customersVendors')}
                    >
                        <span className={styles.DrawerTabText}>
                            Customers & Vendors
                            <span className={styles.DrawerTabCount}>{formatTabCount(cvCount)}</span>
                        </span>
                        {activeTab === 'customersVendors' && <div className={styles.DrawerTabSlider} />}
                    </button>
                    <button
                        type="button"
                        className={clsx(styles.DrawerTab, activeTab === 'lenders' && styles.DrawerTabActive)}
                        onClick={() => setActiveTab('lenders')}
                    >
                        <span className={styles.DrawerTabText}>
                            Lenders
                            <span className={styles.DrawerTabCount}>{formatTabCount(lenderCount)}</span>
                        </span>
                        {activeTab === 'lenders' && <div className={styles.DrawerTabSlider} />}
                    </button>
                    {isRelatedPartiesLocked ? (
                        <Tooltip content="Unlock Related Parties module to view the data" direction="bottom" fitContent small>
                            <div className={clsx(styles.DrawerTab, styles.DrawerTabLocked)}>
                                <span className={styles.DrawerTabText}>
                                    Related Parties
                                    <img src={greyLock} alt="locked" className={styles.DrawerTabLockIcon} />
                                </span>
                            </div>
                        </Tooltip>
                    ) : (
                        <button
                            type="button"
                            className={clsx(styles.DrawerTab, activeTab === 'relatedParties' && styles.DrawerTabActive)}
                            onClick={() => setActiveTab('relatedParties')}
                        >
                            <span className={styles.DrawerTabText}>
                                Related Parties
                                <span className={styles.DrawerTabCount}>{formatTabCount(relatedPartiesCount)}</span>
                            </span>
                            {activeTab === 'relatedParties' && <div className={styles.DrawerTabSlider} />}
                        </button>
                    )}
                </div>

                {activeTab === 'customersVendors' && (
                    <div className={styles.DrawerTable} ref={tableRef}>
                        <div className={clsx(styles.DrawerTableRow, styles.DrawerTableHead)}>
                            <div className={styles.NameCol}>Related Party Name</div>
                            <ColumnHeaderWithFilter
                                label="Status"
                                filterKey="status"
                                activeFilter={activeFilter}
                                hasActiveFilters={statusFilters.length > 0}
                                btnRef={statusFilterBtnRef}
                                onToggle={() => toggleFilter('status')}
                                className={styles.StatusCol}
                            />
                            <ColumnHeaderWithFilter
                                label="Relationship Status"
                                filterKey="relationship"
                                activeFilter={activeFilter}
                                hasActiveFilters={relationshipFilters.length > 0}
                                btnRef={relationshipFilterBtnRef}
                                onToggle={() => toggleFilter('relationship')}
                                className={styles.RelationshipStatusCol}
                            />
                        </div>

                        <SectionToggleButton
                            expanded={vendorsExpanded}
                            label={`Customers in Network (${filteredVendors.length})`}
                            onToggle={() => setVendorsExpanded((prev) => !prev)}
                            disabled={vendorCount === 0}
                        />
                        {vendorsExpanded &&
                            filteredVendors.map((vendor, index) => (
                                <EntityRow
                                    key={`vendor-${index}`}
                                    entity={vendor}
                                    statusOptions={statusOptions}
                                    relationshipStatusOptions={relationshipStatusOptions}
                                />
                            ))}

                        <SectionToggleButton
                            expanded={customersExpanded}
                            label={`Vendors in Network (${filteredCustomers.length})`}
                            onToggle={() => setCustomersExpanded((prev) => !prev)}
                            disabled={customerCount === 0}
                        />
                        {customersExpanded &&
                            filteredCustomers.map((customer, index) => (
                                <EntityRow
                                    key={`customer-${index}`}
                                    entity={customer}
                                    statusOptions={statusOptions}
                                    relationshipStatusOptions={relationshipStatusOptions}
                                />
                            ))}
                    </div>
                )}

                {activeTab === 'lenders' && (
                    <div className={styles.DrawerTable} ref={tableRef}>
                        <div className={clsx(styles.LenderTableRow, styles.DrawerTableHead)}>
                            <div className={styles.NameCol}>Entity Name</div>
                            <div className={styles.LenderAmountCol}>Sanctioned</div>
                            <div className={styles.StatusCol}>Debt Status</div>
                            <div className={styles.LenderSourceCol}>Data Source</div>
                            <div className={styles.LenderDateCol}>Data as of</div>
                        </div>
                        {lenderList.map((lender: any, index: number) => (
                            <div key={lender.lenderOrgId || index} className={styles.LenderTableRow}>
                                <div className={styles.NameCol}>
                                    <div className={styles.LenderCompanyNameCell}>
                                        {lender.lenderName && (
                                            <OfficeIcon
                                                fill="#1C1B1F"
                                                width={16}
                                                height={13}
                                                style={{ flexShrink: 0, minWidth: 16, minHeight: 13 }}
                                            />
                                        )}
                                        <span className={styles.LenderCompanyName}>{lender.lenderName || '-'}</span>
                                    </div>
                                </div>
                                <div className={styles.LenderAmountCol}>{formatAmount(lender.totalAmount)}</div>
                                <div className={styles.StatusCol}>
                                    <LenderStatusPill active={lender.status === 'ACTIVE'} />
                                </div>
                                <div className={styles.LenderSourceCol}>
                                    <SourceChips sources={lender.sources || []} sourceLabels={SOURCE_LABELS} />
                                </div>
                                <div className={styles.LenderDateCol}>{formatLenderDate(lender.latestDataAsOf)}</div>
                            </div>
                        ))}
                        {lenderList.length === 0 && <div className={styles.LenderEmptyState}>No lenders found</div>}
                    </div>
                )}

                {activeTab === 'relatedParties' && (
                    <RelatedPartiesTab data={data} statusOptions={statusOptions} relationshipStatusOptions={relationshipStatusOptions} />
                )}
            </div>

            {activeTab === 'customersVendors' &&
                activeFilter &&
                ReactDOM.createPortal(
                    <FilterDropdown
                        anchorRef={activeFilter === 'status' ? statusFilterBtnRef : relationshipFilterBtnRef}
                        options={visibleFilterOptions}
                        selectedValues={activeFilterValues}
                        search={filterSearch}
                        onSearchChange={setFilterSearch}
                        onToggleOption={activeFilter === 'status' ? handleToggleStatusFilter : handleToggleRelationshipFilter}
                    />,
                    document.body,
                )}
        </SideDrawer>
    );
};

export default RelatedCompaniesNetworkDrawer;

// ─── Sub-components ───────────────────────────────────────────────────────────

type ColumnHeaderWithFilterProps = {
    label: string;
    filterKey: FilterKey;
    activeFilter: FilterKey | null;
    hasActiveFilters: boolean;
    btnRef: React.RefObject<HTMLButtonElement>;
    onToggle: () => void;
    className?: string;
};

const ColumnHeaderWithFilter: React.FC<ColumnHeaderWithFilterProps> = ({
    label,
    filterKey,
    activeFilter,
    hasActiveFilters,
    btnRef,
    onToggle,
    className,
}) => (
    <div className={className}>
        <span>{label}</span>
        <button ref={btnRef} type="button" className={styles.FilterBtn} onClick={onToggle} aria-label={`Filter by ${label}`}>
            <img src={hasActiveFilters || activeFilter === filterKey ? filterIconMUPurpleDark : filterIconMUDark} alt="" />
        </button>
    </div>
);

type EntityRowProps = {
    entity: RelatedCompany;
    statusOptions: StatusOption[];
    relationshipStatusOptions: StatusOption[];
};

const EntityRow: React.FC<EntityRowProps> = ({ entity, statusOptions, relationshipStatusOptions }) => {
    const [hovered, setHovered] = useState(false);
    const statusMeta = getStatusMeta(entity.orgStatus, statusOptions);
    const relStatusMeta = getStatusMeta(entity.orgRelationshipStatus, relationshipStatusOptions);
    const redirectUrl = entity.orgId
        ? `${window.location.origin}/investor/app/portfolio/${entity.orgId}?fetchType=all&page=1&type=scan&tab=aicaReport&selectedTab=overview`
        : '';

    return (
        <div className={styles.DrawerTableRow}>
            <CompanyLinkCell entity={entity} hovered={hovered} setHovered={setHovered} redirectUrl={redirectUrl} />
            <StatusCell statusMeta={statusMeta} fallbackStatus={entity.orgStatus} />
            <StatusCell statusMeta={relStatusMeta} fallbackStatus={entity.orgRelationshipStatus} />
        </div>
    );
};

type CompanyLinkCellProps = {
    entity: RelatedCompany;
    hovered: boolean;
    setHovered: (v: boolean) => void;
    redirectUrl: string;
};

const CompanyLinkCell: React.FC<CompanyLinkCellProps> = ({ entity, hovered, setHovered, redirectUrl }) => {
    const displayName = entity.companyName || entity.orgName || '-';
    return (
        <div className={styles.NameCol}>
            {entity.orgId ? (
                <Tooltip content={AICA_REPORT_AVAILABLE_TOOLTIP} direction="top" usePortal={true} fitContent={true}>
                    <a
                        className={styles.DrawerCompanyLink}
                        href={redirectUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onMouseEnter={() => setHovered(true)}
                        onMouseLeave={() => setHovered(false)}
                    >
                        <div className={styles.DrawerCompanyNameCell}>
                            <img src={hovered ? purpleRedirectIcon : purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                            <span className={styles.DrawerCompanyName}>{displayName}</span>
                        </div>
                    </a>
                </Tooltip>
            ) : (
                <div className={styles.DrawerCompanyNameCell}>
                    <img src={purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                    <span className={styles.DrawerCompanyName}>{displayName}</span>
                </div>
            )}
        </div>
    );
};

type StatusCellProps = {
    statusMeta: StatusOption | null;
    fallbackStatus?: string;
};

const StatusCell: React.FC<StatusCellProps> = ({ statusMeta, fallbackStatus }) => (
    <div className={styles.StatusCol}>
        {statusMeta ? (
            <span className={styles.StatusPill} style={{ backgroundColor: statusMeta.backgroundColor, color: statusMeta.labelColor }}>
                {statusMeta.label}
            </span>
        ) : (
            <span className={clsx(styles.StatusPill, styles.StatusPillFallback)}>{fallbackStatus || '-'}</span>
        )}
    </div>
);

type SectionToggleButtonProps = {
    expanded: boolean;
    label: string;
    onToggle: () => void;
    disabled?: boolean;
};

const SectionToggleButton: React.FC<SectionToggleButtonProps> = ({ expanded, label, onToggle, disabled = false }) => (
    <button type="button" className={styles.SectionRow} onClick={disabled ? undefined : onToggle} disabled={disabled}>
        <span className={styles.SectionCaret}>
            {!disabled && (expanded ? <img src={BLACK_DROPDOWN_CARET} alt="" /> : <img src={caretRight} alt="" />)}
        </span>
        <span>{label}</span>
    </button>
);

const LenderStatusPill: React.FC<{ active: boolean }> = ({ active }) => (
    <span className={`${styles.StatusPill} ${active ? styles.StatusActive : styles.StatusClosed}`}>{active ? 'Active' : 'Closed'}</span>
);

type SourceChipsProps = {
    sources: string[];
    sourceLabels: Record<string, string>;
    maxVisible?: number;
};

const SourceChips: React.FC<SourceChipsProps> = ({ sources, sourceLabels, maxVisible = 2 }) => {
    const visible = sources.slice(0, maxVisible);
    const remaining = sources.length - maxVisible;
    return (
        <div className={styles.SourceChips}>
            {visible.map((src) => (
                <span key={src} className={styles.SourceChip}>
                    {sourceLabels[src] || src}
                </span>
            ))}
            {remaining > 0 && (
                <Tooltip
                    content={sources
                        .slice(maxVisible)
                        .map((s) => sourceLabels[s] || s)
                        .join(', ')}
                    direction="top"
                    usePortal
                    fitContent
                >
                    <span className={styles.SourceChipMore}>+{remaining} more</span>
                </Tooltip>
            )}
        </div>
    );
};

type FilterDropdownProps = {
    anchorRef: React.RefObject<HTMLButtonElement>;
    options: StatusOption[];
    selectedValues: string[];
    search: string;
    onSearchChange: (val: string) => void;
    onToggleOption: (val: string) => void;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({ anchorRef, options, selectedValues, search, onSearchChange, onToggleOption }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);

    const DROPDOWN_OFFSET_Y = 4;
    const DROPDOWN_FALLBACK_WIDTH = 220;
    const SCREEN_EDGE_PADDING = 8;

    useLayoutEffect(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const dropdownWidth = dropdownRef.current?.offsetWidth ?? DROPDOWN_FALLBACK_WIDTH;
        const left = Math.min(rect.left, window.innerWidth - dropdownWidth - SCREEN_EDGE_PADDING);
        setPos({ top: rect.bottom + DROPDOWN_OFFSET_Y, left: Math.max(SCREEN_EDGE_PADDING, left) });
    }, [anchorRef]);

    return (
        <div ref={dropdownRef} data-filter-dropdown="true" className={styles.FilterDropdown} style={{ top: pos.top, left: pos.left }}>
            <div className={styles.FilterSearchWrapper}>
                <SearchIcon width="14" height="14" stroke="#9ca3af" aria-hidden="true" />
                <input type="text" placeholder="Search" value={search} onChange={(e) => onSearchChange(e.target.value)} autoFocus />
            </div>
            <div className={styles.FilterOptionsList}>
                {options.map((opt) => (
                    <label key={opt.value} className={styles.FilterOption}>
                        <input
                            type="checkbox"
                            checked={selectedValues.includes(opt.value.toLowerCase())}
                            onChange={() => onToggleOption(opt.value)}
                        />
                        <span
                            className={styles.StatusPill}
                            style={opt.backgroundColor ? { backgroundColor: opt.backgroundColor, color: opt.labelColor } : undefined}
                        >
                            {opt.label}
                        </span>
                    </label>
                ))}
                {options.length === 0 && <div className={styles.FilterNoOptions}>No options found</div>}
            </div>
        </div>
    );
};
